import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";

export type PartnerCategory = "credito" | "inversiones" | "seguros" | "fit" | "general";

export interface DistripakoPartner {
  id?: string;
  name: string;
  logoUrl: string;
  description?: string;
  website?: string;
  category?: PartnerCategory;
  order?: number;
  active: boolean;
}

const CATEGORY_MAP: Record<PartnerCategory, { label: string; color: "success" | "secondary" | "info" | "warning" | "default" }> = {
  credito: { label: "🏦 Crédito", color: "success" },
  inversiones: { label: "📈 Inversiones", color: "secondary" },
  seguros: { label: "🛡️ Seguros", color: "info" },
  fit: { label: "🏋️ Salud & Fit", color: "warning" },
  general: { label: "🌐 General", color: "default" },
};

const empty: DistripakoPartner = {
  name: "",
  logoUrl: "",
  description: "",
  website: "",
  category: "general",
  order: 0,
  active: true,
};

export default function DistripakoAliadosPage() {
  const [items, setItems] = useState<DistripakoPartner[]>([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<DistripakoPartner>(empty);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState<string>("todos");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "aliados"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DistripakoPartner));
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filteredItems = items.filter((item) => {
    if (filterTab === "todos") return true;
    const cat = item.category || "general";
    return cat === filterTab;
  });

  const handleOpen = (item?: DistripakoPartner) => {
    setCurrent(item ? { ...item, category: item.category || "general" } : { ...empty, order: items.length });
    setOpen(true);
  };

  const handleSave = async () => {
    const id = current.id || doc(collection(db, "aliados")).id;
    await setDoc(doc(db, "aliados", id), {
      ...current,
      id,
      category: current.category || "general",
      updatedAt: serverTimestamp(),
      ...(current.id ? {} : { createdAt: serverTimestamp() }),
    }, { merge: true });
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Eliminar este aliado/cliente del carrusel de la web?")) {
      await deleteDoc(doc(db, "aliados", id));
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const storageRef = ref(storage, `aliados/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setCurrent((prev) => ({ ...prev, logoUrl: url }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>🤝 Aliados y Clientes Web por Categoría</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona los logotipos y asigna su categoría (Crédito, Inversiones, Seguros, Fit) para mostrarlos en sus respectivas secciones web
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
          sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>
          Nuevo Aliado
        </Button>
      </Stack>

      {/* Tabs Filter */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={filterTab} onChange={(_, val) => setFilterTab(val)}>
          <Tab label={`Todos (${items.length})`} value="todos" sx={{ fontWeight: 800 }} />
          <Tab label="🏦 Crédito" value="credito" sx={{ fontWeight: 800 }} />
          <Tab label="📈 Inversiones" value="inversiones" sx={{ fontWeight: 800 }} />
          <Tab label="🛡️ Seguros" value="seguros" sx={{ fontWeight: 800 }} />
          <Tab label="🏋️ Salud & Fit" value="fit" sx={{ fontWeight: 800 }} />
          <Tab label="🌐 General" value="general" sx={{ fontWeight: 800 }} />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(290px, 1fr))" gap={2.5}>
          {filteredItems.map((item) => {
            const catInfo = CATEGORY_MAP[item.category || "general"] || CATEGORY_MAP.general;
            return (
              <Card key={item.id} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Box sx={{ p: 2, bgcolor: "#f8fafc", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 110, borderBottom: "1px solid #f1f5f9" }}>
                  {item.logoUrl ? (
                    <Box component="img" src={item.logoUrl} alt={item.name}
                      sx={{ maxHeight: 65, maxWidth: "100%", objectFit: "contain" }} />
                  ) : (
                    <Typography variant="subtitle1" fontWeight={800} color="text.secondary">
                      {item.name}
                    </Typography>
                  )}
                </Box>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                        <Typography fontWeight={800} fontSize="1rem">{item.name}</Typography>
                        <Chip label={item.active ? "Activo" : "Inactivo"} size="small"
                          color={item.active ? "success" : "default"} sx={{ height: 20, fontSize: "10px" }} />
                      </Stack>

                      <Box mb={1}>
                        <Chip label={catInfo.label} size="small" color={catInfo.color} sx={{ fontWeight: 800, fontSize: "10px", height: 22 }} />
                      </Box>

                      {item.website && (
                        <Typography variant="caption" color="primary" sx={{ textDecoration: "none", display: "block" }}>
                          {item.website}
                        </Typography>
                      )}
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ fontSize: "0.8rem" }}>
                          {item.description}
                        </Typography>
                      )}
                      <Chip label={`Orden: ${item.order}`} size="small" variant="outlined" sx={{ mt: 1, fontSize: "10px" }} />
                    </Box>
                    <Stack>
                      <IconButton size="small" onClick={() => handleOpen(item)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item.id!)}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          {filteredItems.length === 0 && (
            <Box gridColumn="1/-1" textAlign="center" py={8} color="text.secondary">
              <HandshakeIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2, display: "block", mx: "auto" }} />
              <Typography fontWeight={700}>No hay empresas o aliados registrados en esta categoría.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {current.id ? "✏️ Editar Aliado" : "🤝 Nuevo Aliado / Cliente"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField label="Nombre de la Empresa / Marca *" value={current.name} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, name: e.target.value }))} />

            <FormControl fullWidth size="small">
              <InputLabel>Categoría / Sección Web *</InputLabel>
              <Select
                value={current.category || "general"}
                label="Categoría / Sección Web *"
                onChange={(e) => setCurrent((p) => ({ ...p, category: e.target.value as PartnerCategory }))}
              >
                <MenuItem value="credito">🏦 Crédito & Entidades Financieras (/credito)</MenuItem>
                <MenuItem value="inversiones">📈 Inversiones & Portafolios (/inversiones)</MenuItem>
                <MenuItem value="seguros">🛡️ Seguros & Pólizas (/seguros)</MenuItem>
                <MenuItem value="fit">🏋️ Salud & Productos Fit (/fit)</MenuItem>
                <MenuItem value="general">🌐 General / Todos (/)</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Sitio Web o Enlace (Opcional)" value={current.website || ""} fullWidth
              placeholder="https://empresa.com"
              onChange={(e) => setCurrent((p) => ({ ...p, website: e.target.value }))} />

            <TextField label="Descripción o Rubro (Opcional)" value={current.description || ""} fullWidth
              placeholder="Ej: Banco aliado para créditos de libre inversión"
              onChange={(e) => setCurrent((p) => ({ ...p, description: e.target.value }))} />

            <TextField label="Orden de Aparición" type="number" value={current.order || 0} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, order: Number(e.target.value) }))} />

            {/* Logo Image */}
            <Stack direction="row" spacing={2} alignItems="center">
              {current.logoUrl && (
                <Box component="img" src={current.logoUrl} sx={{ width: 100, height: 50, objectFit: "contain", p: 1, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }} />
              )}
              <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : undefined}
                disabled={uploading} sx={{ borderRadius: 2 }}>
                {uploading ? "Subiendo..." : "Subir Logo"}
                <input hidden type="file" accept="image/*"
                  onChange={(e) => e.target.files && uploadImage(e.target.files[0])} />
              </Button>
            </Stack>

            <FormControlLabel
              control={<Switch checked={current.active} onChange={(e) => setCurrent((p) => ({ ...p, active: e.target.checked }))} />}
              label="Visible en la web"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
